import { canUseActivePermissionAction } from './permissionPolicy';
import type { CpqState, QuoteItem, StockReminder } from './types';

export interface InventoryReminderAccess {
  canView: boolean;
  canCreate: boolean;
  canResolve: false;
  contractStatus: 'mock_projection';
  permissionCodes: readonly string[];
}

export interface MockReminderWriteResult {
  state: CpqState;
  createdCount: number;
  incrementedCount: number;
  rejectedCount: number;
  rejectionReason?: string;
}

export function inventoryRunIdForMock(revision: number): string {
  return `mock-inventory-r${revision}`;
}

export function inventoryReminderAccess(
  state: CpqState,
): InventoryReminderAccess {
  const canView = canUseActivePermissionAction(
    state,
    'inventory',
    'inventory:inventory.read',
  );
  const canCreate =
    canView &&
    canUseActivePermissionAction(
      state,
      'inventory',
      'inventory:inventory.adjust',
    );
  return {
    canView,
    canCreate,
    canResolve: false,
    contractStatus: 'mock_projection',
    permissionCodes: canCreate
      ? ['inventory.read', 'inventory.adjust']
      : canView
        ? ['inventory.read']
        : [],
  };
}

function quoteItemForReminder(
  state: CpqState,
  reminder: StockReminder,
): QuoteItem | undefined {
  const quote = state.quotes[reminder.quoteVersionId];
  if (!quote || quote.quoteId !== reminder.quoteId) return undefined;
  const matches = quote.items.filter(
    (item) =>
      item.itemId === reminder.quoteItemId &&
      item.deviceId === reminder.deviceId &&
      item.sku === reminder.skuId,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

export function hasValidStockReminderBinding(
  state: CpqState,
  reminder: StockReminder,
): boolean {
  return Boolean(
    state.catalog.devices.some((device) => device.id === reminder.deviceId) &&
      quoteItemForReminder(state, reminder),
  );
}

export function stockRemindersForDevice(
  state: CpqState,
  deviceId: string,
): StockReminder[] {
  if (!inventoryReminderAccess(state).canView) return [];
  return state.stockReminders
    .filter(
      (reminder) =>
        reminder.deviceId === deviceId &&
        hasValidStockReminderBinding(state, reminder),
    )
    .toSorted(
      (left, right) =>
        right.createdAt.localeCompare(left.createdAt) ||
        right.id.localeCompare(left.id),
    );
}

export function unresolvedStockReminderCount(
  state: CpqState,
  deviceId: string,
): number {
  return stockRemindersForDevice(state, deviceId).filter(
    (reminder) => reminder.status === 'OPEN',
  ).length;
}

export function quarantinedStockReminders(state: CpqState): StockReminder[] {
  if (!inventoryReminderAccess(state).canView) return [];
  return state.stockReminders.filter(
    (reminder) => !hasValidStockReminderBinding(state, reminder),
  );
}

function createMockReminder(
  state: CpqState,
  quoteVersionId: string,
  item: QuoteItem,
  createdAt: string,
): StockReminder {
  return {
    id: `STOCK-${String(state.nextSequence).padStart(4, '0')}`,
    quoteId: state.quotes[quoteVersionId].quoteId,
    quoteVersionId,
    quoteItemId: item.itemId,
    skuId: item.sku,
    deviceId: item.deviceId,
    skuSnapshot: item.sku,
    warehouseId: null,
    inventoryRunId: inventoryRunIdForMock(state.stockSyncRevision),
    status: 'OPEN',
    occurrenceCount: 1,
    requiredQuantity: item.quantity,
    availableQuantity: item.stock,
    rowVersion: 1,
    createdAt,
    resolvedAt: null,
    projectionSource: 'local-storage-mock',
  };
}

function hasValidMockReminderSource(
  state: CpqState,
  quoteVersionId: string,
  item: QuoteItem,
): boolean {
  const quote = state.quotes[quoteVersionId];
  const device = state.catalog.devices.find(
    (candidate) => candidate.id === item.deviceId,
  );
  const uniqueItem =
    quote?.items.filter((candidate) => candidate.itemId === item.itemId)
      .length === 1;
  if (!quote || !device || device.status !== 'active' || !uniqueItem) {
    return false;
  }
  if (
    item.sourceType === 'temporary' ||
    !item.sourceId.trim() ||
    !item.sourceName.trim()
  ) {
    return false;
  }
  return item.sourceType !== 'device' || item.sourceId === device.id;
}

export function recordMockStockReminders(
  state: CpqState,
  quoteVersionId: string,
  createdAt = new Date().toISOString(),
): MockReminderWriteResult {
  if (!inventoryReminderAccess(state).canCreate) {
    throw new Error('当前 Mock 角色无法证明 inventory.remind 数据范围');
  }
  const quote = state.quotes[quoteVersionId];
  if (!quote) throw new Error('报价版本不存在，不能记录库存提醒');
  const shortages = quote.items.filter((item) => item.quantity > item.stock);
  if (!shortages.length) {
    return { state, createdCount: 0, incrementedCount: 0, rejectedCount: 0 };
  }
  const invalidShortages = shortages.filter(
    (item) => !hasValidMockReminderSource(state, quoteVersionId, item),
  );
  if (invalidShortages.length > 0) {
    return {
      state,
      createdCount: 0,
      incrementedCount: 0,
      rejectedCount: invalidShortages.length,
      rejectionReason:
        '报价中存在临时、停用或无法精确绑定目录设备与来源 tuple 的库存项',
    };
  }

  const next = structuredClone(state);
  let createdCount = 0;
  let incrementedCount = 0;
  for (const item of shortages) {
    const existing = next.stockReminders.find(
      (reminder) =>
        reminder.status === 'OPEN' &&
        reminder.quoteId === quote.quoteId &&
        reminder.quoteVersionId === quoteVersionId &&
        reminder.quoteItemId === item.itemId &&
        reminder.deviceId === item.deviceId &&
        reminder.warehouseId === null,
    );
    if (existing) {
      existing.occurrenceCount += 1;
      existing.latestObservedAt = createdAt;
      existing.latestObservedRequiredQuantity = item.quantity;
      existing.latestObservedAvailableQuantity = item.stock;
      existing.latestObservedInventoryRunId = inventoryRunIdForMock(
        next.stockSyncRevision,
      );
      existing.rowVersion += 1;
      incrementedCount += 1;
      continue;
    }
    next.stockReminders.unshift(
      createMockReminder(next, quoteVersionId, item, createdAt),
    );
    next.nextSequence += 1;
    createdCount += 1;
  }
  return { state: next, createdCount, incrementedCount, rejectedCount: 0 };
}
